// Swap history for the terminal — recorded locally at broadcast time.
// On-chain swap indexing doesn't exist yet, so this is honest about scope:
// it is the history of swaps made from THIS browser, and each record is
// stamped with the wallet that broadcast it: one browser can hold several
// accounts, and a trade (or its mempool-pending row) must never show up
// under an account that did not make it.

export interface SwapRecord {
	ts: number
	txid: string
	sellId: string
	sellName: string
	buyId: string
	buyName: string
	amountInRaw: string
	quotedOutRaw: string
	minOutRaw: string
	/// Absent on records written before BTC flows shipped — treat as 'swap'.
	kind?: 'swap' | 'wrap' | 'unwrap'
	/// For a wrap that is step 1 of wrap-then-swap: the token the user was
	/// quoting into, so History can say "come back and swap to X".
	intentBuyId?: string
	intentBuyName?: string
	/// Decoded from the address's own on-chain transactions rather than
	/// recorded at broadcast time — its "quoted" figure is the min-out floor,
	/// not the quote, and rows never enter localStorage.
	fromChain?: boolean
	/// The taproot address that broadcast this record. Records written before
	/// this existed carry none and are never shown — the address's
	/// chain-decoded history already covers them, attributed correctly.
	owner?: string
}

const KEY = 'alkanes-swap-history'
const MAX = 200

function readAll(): SwapRecord[] {
	if (typeof window === 'undefined') {
		return []
	}
	try {
		const parsed = JSON.parse(window.localStorage.getItem(KEY) || '[]')
		return Array.isArray(parsed) ? parsed : []
	} catch {
		return []
	}
}

/// The records this browser broadcast FROM `owner` — nothing for no owner.
export function readHistory(owner: string | null | undefined): SwapRecord[] {
	if (!owner) {
		return []
	}
	return readAll().filter((r) => r.owner === owner)
}

export function recordSwap(owner: string, entry: SwapRecord): void {
	try {
		const next = [{ ...entry, owner }, ...readAll()].slice(0, MAX)
		window.localStorage.setItem(KEY, JSON.stringify(next))
		window.dispatchEvent(new Event('alkanes-history-changed'))
	} catch {
		// Storage full/blocked — history is best-effort.
	}
}
